/**
 * Arithmetic expression parser for the amount field (FR-TXN-07 to FR-TXN-11).
 *
 * The user types `18+64+48` and the app stores both the expression and its evaluated
 * result. `eval()` is prohibited (FR-TXN-09) — this is a hand-written recursive-descent
 * parser, so the prohibition holds by construction rather than by discipline.
 *
 * The permitted character set is digits and `+ - * / ( ) .` only, rejected at the
 * tokeniser before any parsing happens.
 *
 * Evaluation is in BigInt micro-rupees (1e6) rather than paise, so that `2*50` means
 * "2 times ₹50" rather than "200 paise times 5000 paise". The result converts to paise
 * through money.ts's single rounding point.
 *
 * Grammar:
 *   expression := term (('+' | '-') term)*
 *   term       := unary (('*' | '/') unary)*
 *   unary      := '-'? primary
 *   primary    := number | '(' expression ')'
 */

import { divRoundHalfUp, type Paise } from './money';

const MICRO_SCALE = 1_000_000n;
const MICRO_PER_PAISA = 10_000n;

const ALLOWED = /^[0-9+\-*/().\s]*$/;

export type ExpressionErrorCode =
  | 'empty'
  | 'illegal-character'
  | 'unbalanced-bracket'
  | 'unexpected-token'
  | 'trailing-operator'
  | 'malformed-number'
  | 'division-by-zero'
  | 'out-of-range';

export interface ExpressionSuccess {
  ok: true;
  paise: Paise;
  /** True when the input was a plain number rather than an expression. */
  literal: boolean;
}

export interface ExpressionFailure {
  ok: false;
  code: ExpressionErrorCode;
  message: string;
  /** Zero-based offset into the input, when the error has a location. */
  position?: number;
}

export type ExpressionResult = ExpressionSuccess | ExpressionFailure;

const MESSAGES: Record<ExpressionErrorCode, string> = {
  'empty': 'Enter an amount',
  'illegal-character': 'Only digits and + - * / ( ) . are allowed',
  'unbalanced-bracket': 'Unbalanced bracket',
  'unexpected-token': 'Check the expression',
  'trailing-operator': 'Expression ends with an operator',
  'malformed-number': 'Malformed number',
  'division-by-zero': 'Cannot divide by zero',
  'out-of-range': 'Amount is too large',
};

function fail(code: ExpressionErrorCode, position?: number): ExpressionFailure {
  return { ok: false, code, message: MESSAGES[code], position };
}

// ---------------------------------------------------------------------------
// Tokeniser
// ---------------------------------------------------------------------------

type TokenType = 'number' | 'op' | 'lparen' | 'rparen';

interface Token {
  type: TokenType;
  value: string;
  position: number;
}

function tokenise(input: string): Token[] | ExpressionFailure {
  if (!ALLOWED.test(input)) {
    const position = [...input].findIndex((ch) => !/[0-9+\-*/().\s]/.test(ch));
    return fail('illegal-character', position);
  }

  const tokens: Token[] = [];
  let i = 0;

  while (i < input.length) {
    const ch = input[i];

    if (/\s/.test(ch)) {
      i += 1;
      continue;
    }

    if (/[0-9.]/.test(ch)) {
      const start = i;
      while (i < input.length && /[0-9.]/.test(input[i])) i += 1;
      const text = input.slice(start, i);
      if ((text.match(/\./g) ?? []).length > 1 || text === '.') {
        return fail('malformed-number', start);
      }
      tokens.push({ type: 'number', value: text, position: start });
      continue;
    }

    if ('+-*/'.includes(ch)) {
      tokens.push({ type: 'op', value: ch, position: i });
      i += 1;
      continue;
    }

    tokens.push({ type: ch === '(' ? 'lparen' : 'rparen', value: ch, position: i });
    i += 1;
  }

  return tokens;
}

/** "18.5" -> 18_500_000n micro-rupees. Excess decimals are truncated, not rounded. */
function numberToMicro(text: string): bigint {
  const [whole = '', fraction = ''] = text.split('.');
  const wholePart = BigInt(whole === '' ? '0' : whole) * MICRO_SCALE;
  const fractionPart = BigInt(fraction.padEnd(6, '0').slice(0, 6) || '0');
  return wholePart + fractionPart;
}

// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------

class Parser {
  private index = 0;

  constructor(private readonly tokens: Token[]) {}

  private peek(): Token | undefined {
    return this.tokens[this.index];
  }

  private next(): Token | undefined {
    return this.tokens[this.index++];
  }

  atEnd(): boolean {
    return this.index >= this.tokens.length;
  }

  position(): number {
    return this.peek()?.position ?? -1;
  }

  parseExpression(): bigint {
    let left = this.parseTerm();

    for (;;) {
      const token = this.peek();
      if (!token || token.type !== 'op' || !'+-'.includes(token.value)) break;
      this.next();
      const right = this.parseTerm();
      left = token.value === '+' ? left + right : left - right;
    }

    return left;
  }

  private parseTerm(): bigint {
    let left = this.parseUnary();

    for (;;) {
      const token = this.peek();
      if (!token || token.type !== 'op' || !'*/'.includes(token.value)) break;
      this.next();
      const right = this.parseUnary();

      if (token.value === '*') {
        // Both operands are scaled, so the product is scaled twice — rescale once.
        left = divRoundHalfUp(left * right, MICRO_SCALE);
      } else {
        if (right === 0n) throw new ParseError('division-by-zero', token.position);
        left = divRoundHalfUp(left * MICRO_SCALE, right);
      }
    }

    return left;
  }

  private parseUnary(): bigint {
    const token = this.peek();
    if (token?.type === 'op' && (token.value === '-' || token.value === '+')) {
      this.next();
      const value = this.parseUnary();
      return token.value === '-' ? -value : value;
    }
    return this.parsePrimary();
  }

  private parsePrimary(): bigint {
    const token = this.next();

    if (!token) throw new ParseError('trailing-operator');

    if (token.type === 'number') return numberToMicro(token.value);

    if (token.type === 'lparen') {
      const value = this.parseExpression();
      const closing = this.next();
      if (!closing || closing.type !== 'rparen') {
        throw new ParseError('unbalanced-bracket', token.position);
      }
      return value;
    }

    if (token.type === 'rparen') throw new ParseError('unbalanced-bracket', token.position);

    throw new ParseError('unexpected-token', token.position);
  }
}

class ParseError extends Error {
  constructor(
    readonly code: ExpressionErrorCode,
    readonly position?: number,
  ) {
    super(code);
    this.name = 'ParseError';
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Evaluate an amount expression to paise.
 *
 * Never throws — the UI needs a message and a caret position, not an exception
 * (WD-03). Callers check `result.ok`.
 */
export function evaluateExpression(input: string): ExpressionResult {
  if (input.trim() === '') return fail('empty');

  const tokens = tokenise(input);
  if (!Array.isArray(tokens)) return tokens;
  if (tokens.length === 0) return fail('empty');

  const parser = new Parser(tokens);

  let micro: bigint;
  try {
    micro = parser.parseExpression();
  } catch (error) {
    if (error instanceof ParseError) return fail(error.code, error.position);
    throw error;
  }

  if (!parser.atEnd()) return fail('unexpected-token', parser.position());

  const paise = divRoundHalfUp(micro, MICRO_PER_PAISA);
  if (paise > BigInt(Number.MAX_SAFE_INTEGER) || paise < BigInt(Number.MIN_SAFE_INTEGER)) {
    return fail('out-of-range');
  }

  return {
    ok: true,
    paise: Number(paise),
    literal: tokens.length === 1 && tokens[0].type === 'number',
  };
}

/** True when the input is more than a plain number, and so worth storing verbatim. */
export function isExpression(input: string): boolean {
  const result = evaluateExpression(input);
  return result.ok && !result.literal;
}
